import { ArrowRightIcon } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePublicPlans } from "@hooks/usePublicPlans";
import { useStoreUser } from "@hooks/useStoreUser";
import { Tabs, TabsContent } from "@ui/tabs";
import { useCollisions } from "@/hooks/useCollisions";
import { useLoadTimelineData } from "@/hooks/useLoadTimelineData";
import { getCommittee } from "@/utils/utils";
import { KarTabs } from "../molecules/KarTabs";
import { SidebarPlanItem } from "../molecules/SidebarPlanItem";
import { ExportPlansButton } from "../molecules/exportPlansButton";
import { Button } from "../ui/button";

const TABS = {
  Alla: { label: "Alla", value: "all" },
  Consensus: { label: "Consensus", value: "consensus" },
  LinTek: { label: "LinTek", value: "lintek" },
  StuFF: { label: "StuFF", value: "stuff" },
} as const;

export const SidebarPublicPlans = () => {
  const { user } = useStoreUser();
  const { publicPlans, publicPlansByKar } = usePublicPlans();
  const { t } = useTranslation();
  const { getNumCollisionsForPlan } = useCollisions();
  const { handlePlanClick, handleMultiplePlansClick } = useLoadTimelineData();

  const committee = getCommittee(user.committeeId);
  const kar = committee?.kår;

  const defaultTab = kar === "Övrigt" || !kar ? TABS.Alla : TABS[kar];

  const [karFilter, setKarFilter] = useState(defaultTab.value);

  return (
    <Tabs
      value={karFilter}
      className="flex flex-col flex-1"
      orientation="vertical"
    >
      <div className="px-5 pt-4 pb-2 relative flex flex-col gap-0.5">
        <h3 className="text-lg leading-none font-bold">
          {t("public_plans.title")}
        </h3>
        <div className="absolute right-2 top-3 space-x-3">
          <ExportPlansButton />
          <Button
            variant="outline"
            size="icon"
            className="h-8"
            onClick={() => handleMultiplePlansClick(publicPlans)}
            onAuxClick={() => handleMultiplePlansClick(publicPlans, true)}
          >
            <ArrowRightIcon className="size-4" />
          </Button>
        </div>
        <p className="text-muted-foreground text-sm mt-0.5">
          {t("public_plans.description")}
        </p>
      </div>
      <KarTabs
        tabs={Object.values(TABS)}
        active={karFilter}
        onSelect={setKarFilter}
        className="px-2"
      />
      {/* SECTION - all */}
      <TabsContent value={TABS.Alla.value}>
        {publicPlans.map((plan) => (
          <SidebarPlanItem
            key={plan.id}
            item={plan}
            conflictCount={getNumCollisionsForPlan(plan.id).summary}
            onClick={() =>
              handlePlanClick(plan, plan.userId === user.id ? "edit" : "view")
            }
            onAuxClick={() =>
              handlePlanClick(
                plan,
                plan.userId === user.id ? "edit" : "view",
                true,
              )
            }
          />
        ))}
      </TabsContent>
      <TabsContent value={TABS.Consensus.value}>
        {publicPlansByKar.consensus.map((plan) => (
          <SidebarPlanItem
            key={plan.id}
            item={plan}
            conflictCount={getNumCollisionsForPlan(plan.id).summary}
            onClick={() =>
              handlePlanClick(plan, plan.userId === user.id ? "edit" : "view")
            }
            onAuxClick={() =>
              handlePlanClick(
                plan,
                plan.userId === user.id ? "edit" : "view",
                true,
              )
            }
          />
        ))}
      </TabsContent>
      <TabsContent value={TABS.LinTek.value}>
        {publicPlansByKar.lintek.map((plan) => (
          <SidebarPlanItem
            key={plan.id}
            item={plan}
            conflictCount={getNumCollisionsForPlan(plan.id).summary}
            onClick={() =>
              handlePlanClick(plan, plan.userId === user.id ? "edit" : "view")
            }
            onAuxClick={() =>
              handlePlanClick(
                plan,
                plan.userId === user.id ? "edit" : "view",
                true,
              )
            }
          />
        ))}
      </TabsContent>
      <TabsContent value={TABS.StuFF.value}>
        {publicPlansByKar.stuff.map((plan) => (
          <SidebarPlanItem
            key={plan.id}
            item={plan}
            conflictCount={getNumCollisionsForPlan(plan.id).summary}
            onClick={() =>
              handlePlanClick(plan, plan.userId === user.id ? "edit" : "view")
            }
            onAuxClick={() =>
              handlePlanClick(
                plan,
                plan.userId === user.id ? "edit" : "view",
                true,
              )
            }
          />
        ))}
      </TabsContent>
    </Tabs>
  );
};
